import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

export type Callback = () => void;

export const useCatchError = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const showError = useCallback(
    (errorObj: any) => {
      let { message } = errorObj;
      if (Array.isArray(message)) {
        message = message[0];
      }

      // Ensure message is a valid string
      const validMessage =
        message && typeof message === 'string' ? message : t('An unexpected error occurred');

      enqueueSnackbar(validMessage, {
        variant: 'error',
        persist: true,
        action: (key) => (
          <Button size="small" color="inherit" onClick={() => closeSnackbar(key)}>
            {t('Dismiss')}
          </Button>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar, t]
  );

  const catchError = useCallback(
    (error: Error | Blob, callback?: Callback) => {
      // Capture all errors to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'useCatchError',
          errorType: error instanceof Blob ? 'blob' : 'error',
        },
      });

      if (error instanceof Blob) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const errorMessage = JSON.parse(event.target?.result as string);
            showError(errorMessage);
          } catch (parseError) {
            Sentry.captureException(parseError, {
              tags: {
                component: 'useCatchError',
                action: 'parseBlob',
              },
              extra: {
                blobContent: event.target?.result,
              },
            });
            enqueueSnackbar(t('Unknown error occurred'), {
              variant: 'error',
              persist: true,
              action: (key) => (
                <Button size="small" color="inherit" onClick={() => closeSnackbar(key)}>
                  {t('Dismiss')}
                </Button>
              ),
            });
          }
        };
        reader.readAsText(error);
        return;
      }

      showError(error);

      if (callback && typeof callback === 'function') {
        callback();
      }
    },
    [showError, enqueueSnackbar, t, closeSnackbar]
  );

  return catchError;
};
